import { Loading } from "@/components/Loading/Loading";
import { TermsAndPolicyDialog } from "@/components/TermsAndPolicyDialog/TermsAndPolicyDialog";
import { useGetUserConsentStatusV1Query, UserConsentStatusRead } from "@/store/clientApi";
import { FC, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

/**
 * Returns true if any required consent is missing (i.e. not accepted).
 */
const isConsentRequired = (statuses: UserConsentStatusRead[] | undefined) =>
  statuses?.some((s) => s.document.required_for_access && s.status !== "accepted") ?? false;

/**
 * ConsentGuard is a component that checks if the user has accepted all required consents before allowing access to its children components.
 * If any required consent is not accepted, it displays the TermsAndPolicyDialog to prompt the user to accept the necessary consents.
 *
 * Note: This component only guards based on user consents not Organisation level consents.
 * @param children - The child components that require consent to access.
 */
export const ConsentGuard: FC<Props> = ({ children }) => {
  const { data: userConsentStatuses, isLoading: userConsentLoading, refetch: refetchUserConsent } = useGetUserConsentStatusV1Query();

  if (userConsentLoading) {
    return <Loading />;
  }

  if (isConsentRequired(userConsentStatuses)) {
    return (
      <>
        <TermsAndPolicyDialog open onAccepted={() => refetchUserConsent()} />
        {children}
      </>
    );
  }

  return <>{children}</>;
};
